import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatContext = createContext<any>(null);

/**
 * ChatProvider manages chat intent signals.
 *
 * Each action bumps a unique signal ID so consumers can detect new
 * intents via useEffect dependencies, even if the payload is identical.
 */
export const ChatProvider = ({ children }: any) => {
  // Signal counter -- incremented on every intent so useEffect always fires
  const [signalId, setSignalId] = useState(0);

  // Intent type: null | 'open' | 'openWithProduct' | 'addToAssistant'
  const [intent, setIntent] = useState<any>(null);

  // Payload for product-related intents
  const [payload, setPayload] = useState<any>(null);

  const openChatbot = useCallback(() => {
    setIntent('open');
    setPayload(null);
    setSignalId((id) => id + 1);
  }, []);

  const openChatWithProduct = useCallback((productInfo: any) => {
    setIntent('openWithProduct');
    setPayload(productInfo);
    setSignalId((id) => id + 1);
  }, []);

  const addToAssistant = useCallback((productInfo: any) => {
    setIntent('addToAssistant');
    setPayload(productInfo);
    setSignalId((id) => id + 1);
  }, []);

  /** Called by ChatWidget after it has processed the current signal */
  const resetIntent = useCallback(() => {
    setIntent(null);
    setPayload(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        signalId,
        intent,
        payload,
        openChatbot,
        openChatWithProduct,
        addToAssistant,
        resetIntent,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return ctx;
};

export default ChatContext;
